import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { getContactsByCustomerId } from '@/lib/demo-data/customer-communications-contacts';
import { mapContactApiToUi } from '@/lib/mappers/customer-detail';
import type {
  ContactApi,
  ContactsApiResponse,
  StubResponse,
} from '@/types/api/customer-detail';
import { isStubResponse } from '@/types/api/customer-detail';
import type { ContactUI } from '@/types/ui/customer-detail';
import { useApi } from './useApi';
import { useModeAwareQuery } from './useModeAwareQuery';

interface ContactInput {
  title?: string;
  firstName: string;
  surname: string;
  email: string;
  phone: string;
  mobile?: string;
  preferredContactMethod?: string;
  jobTitle?: string;
  company?: string;
  notes?: string;
  isPrimary: boolean;
}

interface ContactUpdateInput {
  title?: string;
  firstName?: string;
  surname?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  preferredContactMethod?: string;
  jobTitle?: string;
  company?: string;
  notes?: string;
  isPrimary?: boolean;
}

/**
 * Mode-aware query hook for fetching customer contacts.
 * Returns contacts reactively via useModeAwareQuery, respecting the
 * current screen's Demo/Live toggle.
 */
export function useCustomerContactsList(customerId: string) {
  const { apiGet } = useApi();
  const mockContacts =
    getContactsByCustomerId(customerId).map(mapContactApiToUi);

  return useModeAwareQuery<ContactUI[], Error>(
    ['customer-contacts', customerId],
    async ({ signal }) => {
      if (!customerId || customerId === '0') return [];
      const response = await apiGet<ContactsApiResponse | StubResponse>(
        `/api/v1/customers/${customerId}/contacts`,
        undefined,
        signal,
      );

      if (isStubResponse(response)) {
        return getContactsByCustomerId(customerId).map(mapContactApiToUi);
      }

      return response.contacts.map(mapContactApiToUi);
    },
    mockContacts,
    {
      enabled: !!customerId && customerId !== '0',
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

/**
 * Mode-aware query hook for fetching contacts for multiple customers.
 * Returns combined contacts from all specified customer IDs.
 */
export function useBulkCustomerContactsList(customerIds: string[]) {
  const { apiGet } = useApi();

  // Get mock contacts for all customers
  const mockContacts = customerIds.flatMap((id) =>
    getContactsByCustomerId(id).map(mapContactApiToUi),
  );

  const sortedCustomerIds = [...customerIds].sort((a, b) => a.localeCompare(b));

  return useModeAwareQuery<ContactUI[], Error>(
    ['bulk-customer-contacts', ...sortedCustomerIds],
    async ({ signal }) => {
      if (!customerIds || customerIds.length === 0) return [];

      // Fetch contacts for each customer in parallel
      const contactPromises = customerIds.map(async (customerId) => {
        if (!customerId || customerId === '0') return [];

        try {
          const response = await apiGet<ContactsApiResponse | StubResponse>(
            `/api/v1/customers/${customerId}/contacts`,
            undefined,
            signal,
          );

          if (isStubResponse(response)) {
            return getContactsByCustomerId(customerId).map(mapContactApiToUi);
          }

          return response.contacts.map(mapContactApiToUi);
        } catch (error) {
          console.error(
            `Failed to fetch contacts for customer ${customerId}:`,
            error,
          );
          return [];
        }
      });

      const contactsArrays = await Promise.all(contactPromises);
      return contactsArrays.flat();
    },
    mockContacts,
    {
      enabled: customerIds.length > 0,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

/**
 * Hook for customer contact mutation operations (create, update, delete).
 * Uses authenticated API calls with proper headers.
 */
export function useCustomerContacts(customerId: string) {
  const { apiPost, apiPut, apiDelete } = useApi();
  const queryClient = useQueryClient();

  const invalidateContacts = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['customer-contacts', customerId],
    });
    queryClient.invalidateQueries({
      queryKey: ['customer-detail', customerId],
    });
    queryClient.invalidateQueries({ queryKey: ['case-contacts', customerId] });
  }, [queryClient, customerId]);

  const createContact = useCallback(
    async (contact: ContactInput): Promise<ContactApi> => {
      const result = await apiPost<ContactApi>(
        `/api/v1/customers/${customerId}/contacts`,
        contact,
      );
      invalidateContacts();
      return result;
    },
    [customerId, apiPost, invalidateContacts],
  );

  const updateContact = useCallback(
    async (contactId: string, contact: ContactUpdateInput) => {
      const result = await apiPut<unknown>(
        `/api/v1/customers/${customerId}/contacts/${contactId}`,
        contact,
      );
      invalidateContacts();
      return result;
    },
    [customerId, apiPut, invalidateContacts],
  );

  const deleteContact = useCallback(
    async (contactId: string) => {
      const result = await apiDelete<void>(
        `/api/v1/customers/${customerId}/contacts/${contactId}`,
      );
      invalidateContacts();
      return result;
    },
    [customerId, apiDelete, invalidateContacts],
  );

  const setContactAsPrimary = useCallback(
    async (contactId: string) => {
      return updateContact(contactId, { isPrimary: true });
    },
    [updateContact],
  );

  return {
    createContact,
    updateContact,
    deleteContact,
    setContactAsPrimary,
  };
}
